import {JSX} from "react";

/**
 * StructuredData Component Props
 */
export interface StructuredDataProps {
    /**
     * Array of JSON-LD schemas as HTML strings from Content API
     * Each schema includes <script type="application/ld+json"> tag
     */
    schemas: Array<string>;
}

/**
 * Parse HTML script tag to extract content and attributes
 */
function parseScriptTag(html: string): {content: string; attributes: Record<string, string>} | null {
    // Match <script ...>content</script>
    const scriptRegex = /<script([^>]*)>([\s\S]*?)<\/script>/i;
    const match = html.match(scriptRegex);

    if (!match) {
        return null;
    }

    const attributesString = match[1] || "";
    const content = match[2] || "";

    // Parse attributes
    const attributes: Record<string, string> = {};
    const attrRegex = /(\w+)=["']([^"']+)["']/g;
    let attrMatch;

    while ((attrMatch = attrRegex.exec(attributesString)) !== null) {
        attributes[attrMatch[1]] = attrMatch[2];
    }

    return {content, attributes};
}

/**
 * Check if string is valid JSON
 */
function isJSON(str: string): boolean {
    try {
        JSON.parse(str);
        return true;
    } catch {
        return false;
    }
}

/**
 * Server Component for rendering JSON-LD structured data from Content API
 * Supports two formats:
 * 1. HTML string with <script> tag (attributes are preserved)
 * 2. Plain JSON string (wrapped in <script type="application/ld+json">)
 *
 * @example
 * <StructuredData schemas={pageData.seo?.seo_schemas || []} />
 */
export function StructuredData({schemas}: StructuredDataProps): JSX.Element {
    if (!schemas || schemas.length === 0) {
        return <></>;
    }

    return (
        <>
            {schemas.map((schemaData, index) => {
                // Try to parse as HTML with script tag
                const parsed = parseScriptTag(schemaData);

                if (parsed) {
                    // Format 1: HTML with <script> tag - use extracted content and attributes
                    return (
                        <script
                            key={index}
                            {...parsed.attributes}
                            dangerouslySetInnerHTML={{
                                __html: parsed.content,
                            }}
                        />
                    );
                }

                // Format 2: Plain JSON - validate and wrap in script tag
                if (isJSON(schemaData.trim())) {
                    return (
                        <script
                            key={index}
                            type="application/ld+json"
                            dangerouslySetInnerHTML={{
                                __html: schemaData,
                            }}
                        />
                    );
                }

                // Invalid format
                console.warn("Invalid schema format (not HTML with <script> nor valid JSON):", schemaData);
                return null;
            })}
        </>
    );
}
